import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { KeyRound, RefreshCw, UserX, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTAIL_LABEL = { entreprise: 'Portail entreprise', financeur: 'Portail financeur' };
const PORTAIL_LOGIN = { entreprise: '/login-entreprise', financeur: '/login-financeur' };
const ROLE_PORTAIL = { client: 'entreprise', financeur: 'financeur' };

export const AccesPortailContacts = ({ client, portails = ['entreprise'] }) => {
  const [acces, setAcces] = useState([]);
  const [credentials, setCredentials] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API_URL}/clients/${client.id}/acces-portail`).then(r => setAcces(r.data)).catch(() => {});
  useEffect(() => { load(); setCredentials(null); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (contactId, fn, okMsg) => {
    setBusy(contactId);
    try {
      const res = await fn();
      if (res?.data?.password) setCredentials({ contactId, ...res.data });
      toast.success(okMsg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(null); }
  };

  const contacts = (client.contacts || []).filter(c => c.id);
  if (contacts.length === 0) {
    return (
      <div className="p-3 rounded-lg bg-slate-50 border border-dashed border-slate-300 text-xs text-slate-600" data-testid="acces-portail-aucun-contact">
        Aucun contact sur cette fiche. Cliquez sur <b>Modifier</b> et ajoutez un contact <b>avec une adresse email</b> : vous pourrez ensuite lui activer l'accès au portail ici.
      </div>
    );
  }
  const sansEmail = contacts.filter(c => !(c.email || '').trim()).length;

  return (
    <div className="space-y-2" data-testid="acces-portail-section">
      <p className="text-xs text-slate-500">Seuls les contacts de cette fiche peuvent accéder au portail (suivi pédagogique des candidats). Un email est obligatoire pour créer l'accès.{portails.length > 1 && ' Cette société est à la fois entreprise et financeur : choisissez le portail à ouvrir pour chaque contact.'}</p>
      {sansEmail > 0 && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="acces-sans-email-info">{sansEmail} contact(s) sans email : cliquez sur <b>Modifier</b> pour renseigner l'email, puis revenez activer l'accès.</p>}
      {contacts.map(c => {
        const compte = acces.find(a => a.contact_id === c.id);
        const noEmail = !(c.email || '').trim();
        return (
          <div key={c.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-200 bg-white" data-testid={`acces-contact-${c.id}`}>
            <div className="min-w-0">
              <p className="text-sm text-slate-900 truncate">{c.nom} {c.prenom} <span className={`text-xs ${noEmail ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>{c.email || 'email manquant'}</span></p>
              <p className={`text-[11px] ${compte ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`acces-statut-${c.id}`}>{compte ? `Accès actif · ${PORTAIL_LABEL[ROLE_PORTAIL[compte.role]] || 'Portail'} · ${compte.email}` : 'Pas d\'accès'}</p>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              {!compte ? (
                portails.map(p => (
                  <Button key={p} size="sm" variant="outline" disabled={noEmail || busy === c.id} title={noEmail ? 'Renseignez un email sur ce contact (bouton Modifier)' : 'Créer le compte et afficher le mot de passe'} onClick={() => run(c.id, () => axios.post(`${API_URL}/clients/${client.id}/acces-portail/${c.id}`, null, { params: { portail: p } }), 'Accès créé')} data-testid={`acces-activer-${p}-${c.id}`}>
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> {portails.length > 1 ? `Accès ${p}` : "Activer l'accès"}
                  </Button>
                ))
              ) : (
                <>
                  <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => run(c.id, () => axios.post(`${API_URL}/clients/${client.id}/acces-portail/${c.id}/reset`), 'Mot de passe réinitialisé')} data-testid={`acces-reset-${c.id}`}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Réinitialiser
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={busy === c.id} onClick={() => run(c.id, () => axios.delete(`${API_URL}/clients/${client.id}/acces-portail/${c.id}`), 'Accès supprimé')} data-testid={`acces-supprimer-${c.id}`}>
                    <UserX className="w-3.5 h-3.5 mr-1" /> Retirer
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}
      {credentials && (
        <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-200 text-sm" data-testid="acces-credentials">
          <p className="text-xs font-medium text-cyan-800 mb-1">Identifiants à transmettre au contact (affichés une seule fois) — connexion sur <span className="font-mono">{window.location.origin}{PORTAIL_LOGIN[credentials.portail] || '/login'}</span></p>
          <p className="font-mono text-slate-800">{credentials.email}</p>
          <p className="font-mono text-slate-800 flex items-center gap-2">Mot de passe : <span data-testid="acces-password">{credentials.password}</span>
            <button type="button" className="text-cyan-700 hover:text-cyan-900" onClick={() => { navigator.clipboard?.writeText(`${PORTAIL_LABEL[credentials.portail] || 'Portail'} : ${window.location.origin}${PORTAIL_LOGIN[credentials.portail] || '/login'}\nIdentifiant : ${credentials.email}\nMot de passe : ${credentials.password}`); toast.success('Copié'); }} data-testid="acces-copy-btn"><Copy className="w-3.5 h-3.5" /></button>
          </p>
        </div>
      )}
    </div>
  );
};
